/* KCI Event Tagging › Notification Config (KCI_API_DOC §8).
   Per-merchant notification settings that gate whether KCI emails actually go out:
     - customerNotificationEnabled  (opt-in, default OFF) — customer emails only send
       when BOTH this AND the event's customerNotificationsEnabled are true.
     - clientNotificationEnabled    — whether the brand/client email goes out.
     - clientEmails[]               — who the client email goes to; empty falls back to
       the merchant's registered contact email.
   Pick a merchant → load its config (GET), edit, Save (PUT). A never-configured
   merchant returns defaults (customer OFF); PUT 404s if it has no merchant_config. */
import React, { useEffect, useMemo, useState } from 'react'
import { useSnackbar } from 'notistack'
import {
  Box, Grid, Stack, Typography, Autocomplete, TextField, Chip, Switch,
  Button, CircularProgress, Paper, Avatar, Badge,
} from '@mui/material'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import PageTitle from '../../components/PageTitle'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../redux/commonApiCalls'
import { useMerchantConfigQuery, useSaveMerchantConfigMutation } from '../../redux/kciApiSlice'
import { SectionCard, MUTED, primaryBtnSx, errMsg, sepOption } from './kciUi'

const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)

// Icon-badge toggle card (merchant-details Notifications look): colored avatar,
// title + description, and the switch with an On/Off status chip.
const ToggleCard = ({ icon, color, title, desc, checked, onChange }) => (
  <Paper variant='outlined' sx={{
    p: 2, borderRadius: 2, height: '100%', display: 'flex', alignItems: 'center', gap: 2,
    transition: 'box-shadow .2s ease', '&:hover': { boxShadow: 3 },
  }}>
    <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.main`, width: 44, height: 44, flex: '0 0 auto' }}>{icon}</Avatar>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{title}</Typography>
      <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.45, mt: 0.25 }}>{desc}</Typography>
    </Box>
    <Stack alignItems='center' spacing={0.5} sx={{ flex: '0 0 auto' }}>
      <Switch checked={checked} onChange={onChange} />
      <Chip size='small' label={checked ? 'On' : 'Off'} color={checked ? 'success' : 'default'}
        variant={checked ? 'filled' : 'outlined'} sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
    </Stack>
  </Paper>
)

export default function NotificationConfig() {
  const { enqueueSnackbar } = useSnackbar()
  const { data: merchants = [], isLoading: merchantsLoading } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const [merchant, setMerchant] = useState(null)

  const merchantOid = merchant?.id
  const { data: config, isFetching, isError, error } = useMerchantConfigQuery(merchantOid, { skip: !merchantOid })
  const [saveConfig, { isLoading: saving }] = useSaveMerchantConfigMutation()

  // editable form state, hydrated from the loaded config
  const [customerOn, setCustomerOn] = useState(false)
  const [clientOn, setClientOn] = useState(true)
  const [emails, setEmails] = useState([])
  const [emailDraft, setEmailDraft] = useState('')

  useEffect(() => {
    if (config) {
      setCustomerOn(!!config.customerNotificationEnabled)
      setClientOn(config.clientNotificationEnabled !== false)
      setEmails(Array.isArray(config.clientEmails) ? config.clientEmails : [])
    }
  }, [config])

  useEffect(() => {
    if (isError) enqueueSnackbar(errMsg(error, 'Could not load merchant config.'), { variant: 'error' })
  }, [isError, error, enqueueSnackbar])

  const merchantOptions = useMemo(
    () => (merchants || []).filter((m) => m.is_deleted === '0' || m.is_deleted === undefined),
    [merchants],
  )

  const addEmail = (raw) => {
    const v = (raw || '').trim().replace(/,$/, '')
    if (!v) return
    if (!isEmail(v)) { enqueueSnackbar(`"${v}" is not a valid email.`, { variant: 'warning' }); return }
    if (emails.includes(v)) { setEmailDraft(''); return }
    setEmails((e) => [...e, v]); setEmailDraft('')
  }

  const onSave = async () => {
    if (!merchantOid) { enqueueSnackbar('Pick a merchant first.', { variant: 'warning' }); return }
    const pending = emailDraft.trim()
    const finalEmails = pending && isEmail(pending) ? [...emails, pending] : emails
    try {
      await saveConfig({
        merchantOid,
        body: {
          customerNotificationEnabled: customerOn,
          clientNotificationEnabled: clientOn,
          clientEmails: finalEmails,
        },
      }).unwrap()
      setEmails(finalEmails); setEmailDraft('')
      enqueueSnackbar('Notification config saved.', { variant: 'success' })
    } catch (err) {
      // 404 = merchant has no merchant_config document (KCI_API_DOC §8)
      if (err?.status === 404) enqueueSnackbar('This merchant has no merchant_config yet — it must be created before KCI config can be saved.', { variant: 'error' })
      else enqueueSnackbar(errMsg(err, 'Could not save config.'), { variant: 'error' })
    }
  }

  return (
    <Box>
      <PageTitle>KCI Management · Notification Config</PageTitle>

      <SectionCard sx={{ mb: 2 }}>
        <Stack direction='row' alignItems='center' spacing={2} sx={{ mb: 1.75 }}>
          <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', width: 40, height: 40 }}><StorefrontOutlinedIcon /></Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Merchant</Typography>
            <Typography sx={{ fontSize: 11.5, color: MUTED }}>Pick a merchant to view and edit its KCI notification settings.</Typography>
          </Box>
        </Stack>
        <Autocomplete
          size='small' options={merchantOptions} value={merchant} loading={merchantsLoading}
          onChange={(e, v) => setMerchant(v)}
          getOptionLabel={(m) => `${m.name}${m.companyName ? ` — ${m.companyName}` : ''}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderOption={sepOption((m) => `${m.name}${m.companyName ? ` — ${m.companyName}` : ''}`, (m) => m.id)}
          renderInput={(p) => <TextField {...p} placeholder='Search and select a merchant' />}
        />
      </SectionCard>

      {!merchantOid ? (
        <SectionCard>
          <Stack alignItems='center' spacing={1.5} sx={{ py: 6, textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', width: 64, height: 64 }}>
              <NotificationsNoneOutlinedIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: 'text.primary' }}>No merchant selected</Typography>
            <Typography sx={{ fontSize: 12.5, color: MUTED, maxWidth: 420 }}>
              Search and pick a merchant above to view and edit its KCI notification settings — the customer / client toggles and the email recipients.
            </Typography>
          </Stack>
        </SectionCard>
      ) : isFetching ? (
        <SectionCard><Stack alignItems='center' py={3}><CircularProgress size={22} /></Stack></SectionCard>
      ) : (
          <Stack spacing={2}>
            {/* Notification toggles */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ToggleCard
                  icon={<PersonOutlineIcon />} color='secondary'
                  title='Customer notifications'
                  desc="Opt-in, default OFF. A customer email sends only when this AND the event's customer-notify toggle are both ON."
                  checked={customerOn} onChange={(e) => setCustomerOn(e.target.checked)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ToggleCard
                  icon={<StorefrontOutlinedIcon />} color='primary'
                  title='Client notifications'
                  desc='Whether the brand/client KCI email goes out for this merchant.'
                  checked={clientOn} onChange={(e) => setClientOn(e.target.checked)}
                />
              </Grid>
            </Grid>

            {/* Client email recipients */}
            <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction='row' alignItems='center' spacing={2} sx={{ mb: 1.75 }}>
                <Avatar sx={{ bgcolor: 'success.light', color: 'success.main', width: 44, height: 44, flex: '0 0 auto' }}><MailOutlineIcon /></Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Client email recipients</Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>Who receives the brand/client KCI email — empty falls back to the merchant's registered contact.</Typography>
                </Box>
                <Badge badgeContent={emails.length} color={emails.length ? 'success' : 'default'} max={99}>
                  <MailOutlineIcon color={emails.length ? 'success' : 'disabled'} />
                </Badge>
              </Stack>

              {emails.length > 0 ? (
                <Stack direction='row' flexWrap='wrap' gap={1} sx={{ mb: 1.75 }}>
                  {emails.map((em) => (
                    <Chip key={em} label={em} color='success' variant='filled' size='small'
                      onDelete={() => setEmails((e) => e.filter((x) => x !== em))}
                      sx={{ borderRadius: '16px', fontWeight: 500, '& .MuiChip-label': { px: 1.25 } }} />
                  ))}
                </Stack>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, mb: 1.75, backgroundColor: 'grey.50', borderRadius: 1, border: '1px dashed', borderColor: 'grey.300' }}>
                  <MailOutlineIcon color='disabled' fontSize='small' />
                  <Typography variant='body2' color='text.secondary'>None set — KCI emails fall back to the merchant's registered contact email.</Typography>
                </Box>
              )}

              <Stack direction='row' spacing={1}>
                <TextField size='small' fullWidth placeholder='Add an email and press Enter' value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addEmail(emailDraft) } }} />
                <Button variant='outlined' onClick={() => addEmail(emailDraft)} sx={{ textTransform: 'none' }}>Add</Button>
              </Stack>
            </Paper>

            {/* Save */}
            <Stack direction='row' justifyContent='flex-end'>
              <Button variant='contained' sx={primaryBtnSx} onClick={onSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save config'}
              </Button>
            </Stack>
          </Stack>
      )}
    </Box>
  )
}
